import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import PeriodicReviewFinish from '@/features/periodic-review/components/finish';
import { PERIODIC_REVIEW_PATHS } from '@/features/periodic-review/constants';
import { PeriodicReviewProvider } from '@/features/periodic-review/context';
import { useProfileFcaQuestionnaireEligibility } from '@/hooks/queries/use-profile-fca-questionnaire';
import {
  useProfilePeriodicReview,
  useProfilePeriodicReviewAml,
  useProfilePeriodicReviewConfirmation,
  useProfilePeriodicReviewIdentity,
  useProfilePeriodicReviewNationalClientIdentifier,
} from '@/hooks/queries/use-profile-periodic-review';
import ErrorPage from '@/pages/error';
import LoadingPage from '@/pages/loading';
import { AccountType } from '@/utils/account-type';

const POST_CONFIRMATION_PATHS = [PERIODIC_REVIEW_PATHS.VERIFICATION, PERIODIC_REVIEW_PATHS.COMPLETE];

const PeriodicReviewGuard = () => {
  const { isLoadingError: isErrorReview, isLoading: isLoadingReview, data: periodicReview } = useProfilePeriodicReview();
  const isDue = periodicReview?.status === 'DUE';
  const { isLoadingError: isErrorDraft, isLoading: isLoadingDraft, data: draft } = useProfilePeriodicReviewIdentity(isDue);
  // Loaded here so a step never mistakes an answer that failed to load for the customer not having one
  const { isLoadingError: isErrorNationalClientIdentifier, isLoading: isLoadingNationalClientIdentifier } = useProfilePeriodicReviewNationalClientIdentifier(isDue);
  const { isLoadingError: isErrorAml, isLoading: isLoadingAml } = useProfilePeriodicReviewAml(isDue);
  // Not waited for, as it starts when a save moves the draft to the UK, and showing a loading page would unmount the step saving it
  const { isLoadingError: isErrorFca } = useProfileFcaQuestionnaireEligibility(isDue ? draft?.identity?.addressOfResidency?.country : undefined, AccountType.SPOT);
  const {
    isLoadingError: isErrorConfirmation,
    isLoading: isLoadingConfirmation,
    data: confirmation,
  } = useProfilePeriodicReviewConfirmation(!isLoadingReview && !isErrorReview && !isDue);
  const location = useLocation();
  const { t } = useTranslation();
  const isError = isErrorReview || isErrorDraft || isErrorNationalClientIdentifier || isErrorAml || isErrorFca || isErrorConfirmation;
  const isLoading = isLoadingReview || isLoadingDraft || isLoadingNationalClientIdentifier || isLoadingAml || isLoadingConfirmation;
  const isNothingToReview = !isError && !isLoading && !isDue && !confirmation;
  const isPostConfirmationPath = POST_CONFIRMATION_PATHS.includes(location.pathname);

  useEffect(() => {
    if (isNothingToReview) window.location.assign(import.meta.env.VITE_APP_EXCHANGE_URL);
  }, [isNothingToReview]);

  if (isError) return <ErrorPage />;

  if (isLoading || isNothingToReview) return <LoadingPage />;

  if (isDue) {
    if (isPostConfirmationPath) return <Navigate to={PERIODIC_REVIEW_PATHS.BASE} replace />;

    // The API returns no draft to a customer whose account status doesn't let them complete a review, or who has no identity
    if (!draft?.isAvailable) return <ErrorPage>{t('periodicReview.unavailable')}</ErrorPage>;

    return (
      <PeriodicReviewProvider>
        <Outlet />
      </PeriodicReviewProvider>
    );
  }

  if (!confirmation?.isFinished) return <PeriodicReviewFinish />;

  if (isPostConfirmationPath) return <Outlet />;

  // A customer who has since verified again is sent on by the verification page
  return <Navigate to={confirmation.verificationSteps.length > 0 ? PERIODIC_REVIEW_PATHS.VERIFICATION : PERIODIC_REVIEW_PATHS.COMPLETE} replace />;
};
PeriodicReviewGuard.displayName = 'PeriodicReviewGuard';

export default PeriodicReviewGuard;
